/**
 * @module lib/get-itemized-closing-costs
 */
import { LOAN_TYPE_FHA, LOAN_TYPE_VA } from '@nerdwallet/home-constants';
import getVaFundingFeePercent from './get-va-funding-fee';
import { UPFRONT_CREDIT_REPORT_FEE, UPFRONT_FHA_MIP } from '../data/constants';

/**
 * @typedef {object} ItemizedClosingCosts
 * @property {number} originationCharges  Origination charges for the loan (from costs-model)
 * @property {number} titleServicesAndInsurance  Title services/insurance costs
 * @property {number} totalSettlementServices  Summed settlement services (upfrontRequiredServices, titleServicesAndInsurance & transferTaxAndInsurance)
 * @property {number} transferTaxAndInsurance  (title) transfer tax, property tax, and homeowners insurance
 * @property {number} upfrontMortgageInsurance  Upfront Mortgage Insurance Premium
 * @property {number} upfrontRequiredServices  Appraisal and credit report fees
 */

/**
 * Get itemized closing costs for the loan
 * @param {object} data
 * @param {number} data.annualInterestRate  The annual interest rate (in decimal)
 * @param {number} data.loanAmount  The loan amount
 * @param {number} data.monthlyHomeownersInsurance  The monthly homeowners insurance payment
 * @param {number} data.yearlyPropertyTax The yearly property tax (in dollars)
 * @param {number} data.originationCharges Origination charges for the loan (from costs-model)
 * @param {number} data.appraisalFee Appraisal fees for the loan (from costs-model)
 * @param {number} data.stateTransferTax Appraisal fees for the loan (from costs-model)
 *
 * @returns {ItemizedClosingCosts}
 */
export default function getItemizedClosingCosts(calculateMortgageInsurance, _ref) {
  var annualInterestRate = _ref.annualInterestRate,
    loanAmount = _ref.loanAmount,
    loanType = _ref.loanType,
    monthlyHomeownersInsurance = _ref.monthlyHomeownersInsurance,
    veteranType = _ref.veteranType,
    firstVaLoan = _ref.firstVaLoan,
    yearlyPropertyTax = _ref.yearlyPropertyTax,
    outputDownPayment = _ref.outputDownPayment,
    originationCharges = _ref.originationCharges,
    appraisalFee = _ref.appraisalFee,
    stateTransferTax = _ref.stateTransferTax;
  // Title insurance & services
  var titleSearchAndLenders = loanAmount * 0.002;
  var ownersTitleInsurance = loanAmount * 0.003;
  var titleServicesAndInsurance = titleSearchAndLenders + ownersTitleInsurance;

  // Transfer tax, property tax, homeowners insurance and prepaid interest charges
  var transferTax = loanAmount * stateTransferTax;
  var escrowPropertyTaxes = yearlyPropertyTax / 4;
  var prepaidInterestCharges = loanAmount * (annualInterestRate / 12);
  var escrowHomeownersInsurance = monthlyHomeownersInsurance * 2;
  var transferTaxAndInsurance = transferTax + escrowHomeownersInsurance + escrowPropertyTaxes + prepaidInterestCharges + monthlyHomeownersInsurance * 12; // TODO EM-3290 why are including yearly rate here?

  // upfront required services
  var upfrontMortgageInsurance = calculateMortgageInsurance && loanType === LOAN_TYPE_FHA ? loanAmount * UPFRONT_FHA_MIP : 0;
  var upfrontVaFundingFee = loanType === LOAN_TYPE_VA ? loanAmount * getVaFundingFeePercent({
    loanAmount: loanAmount,
    outputDownPayment: outputDownPayment,
    veteranType: veteranType,
    firstVaLoan: firstVaLoan
  }) : 0;
  var upfrontRequiredServices = appraisalFee + UPFRONT_CREDIT_REPORT_FEE + upfrontMortgageInsurance + upfrontVaFundingFee;
  var totalSettlementServices = upfrontRequiredServices + titleServicesAndInsurance + transferTaxAndInsurance;
  return {
    total: loanAmount * 0.03,
    originationCharges: originationCharges,
    titleServicesAndInsurance: titleServicesAndInsurance,
    totalSettlementServices: totalSettlementServices,
    transferTaxAndInsurance: transferTaxAndInsurance,
    upfrontMortgageInsurance: upfrontMortgageInsurance,
    upfrontVaFundingFee: upfrontVaFundingFee,
    upfrontRequiredServices: upfrontRequiredServices
  };
}